import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { CreateTicketPayload, IOrganization, IUser } from '../../../@core/models/model';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { ToastService } from '../../../@core/services/toast.service';
import { OrganizationSetupFormService } from '../../../contract-management/services/organization-setup-from.service';
import { SupportTicketService } from '../../../contract-management/services/support-ticket.service';
import { IconComponent } from "src/app/@core/components/ui/icon/icon.component";

@Component({
    selector: 'app-support-ticket-form',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ButtonComponent,
        InputTextModule,
        Textarea,
        CardModule,
        ToastModule,
        IconComponent
    ],
    standalone: true,
    templateUrl: './support-ticket-form.component.html',
    styleUrl: './support-ticket-form.component.scss',
    providers: [MessageService]
})
export class SupportTicketFormComponent implements OnInit, OnDestroy {

    @Input() isDialog: boolean = false;
    @Output() ticketSubmitted = new EventEmitter<void>();
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
    @ViewChild('scrollableContent') scrollableContent!: ElementRef<HTMLDivElement>;

    supportTicketForm!: FormGroup;
    isSubmitting = false;
    currentUser: IUser | null | undefined = null;
    organizationData: IOrganization | null = null;
    formInitialized = false;
    preservedDescription: string = '';
    preservedTitle: string = '';
    attachmentFiles: File[] = [];
    attachmentPreviews: { file: File; preview: string; isImage: boolean, isVideo: boolean }[] = [];

    constructor(
        private fb: FormBuilder,
        private authService: AuthenticationService,
        private organizationService: OrganizationSetupFormService,
        private supportTicketService: SupportTicketService,
        private toastService: ToastService
    ) {
        this.authService.currentUser.subscribe(user => {
            this.currentUser = user;
            this.initializeFormWithData();
        });
    }

    ngOnInit() { }

    ngOnDestroy() {
        this.attachmentPreviews.forEach(item => {
            if (item.isImage) {
                URL.revokeObjectURL(item.preview);
            }
        });
    }

    private initializeFormWithData() {
        if (this.currentUser) {
            this.fetchOrganizationData();
        } else {
            this.initializeForm();
        }
    }

    private fetchOrganizationData() {
        if (this.formInitialized) return;

        this.organizationService.fetchOrganizationSetupData().subscribe((response) => {
            this.organizationData = response.data;
            this.initializeForm();
        });
    }

    private initializeForm() {
        if (this.formInitialized) return;

        const userName = this.currentUser
            ? (this.currentUser.fullName || `${this.currentUser.firstName || ''} ${this.currentUser.lastName || ''}`.trim())
            : '';
        const userEmail = this.currentUser?.email || '';
        const companyName = this.organizationData?.name || '';

        this.supportTicketForm = this.fb.group({
            name: [{ value: userName, disabled: true }, [Validators.required, Validators.minLength(2)]],
            company: [{ value: companyName, disabled: true }, [Validators.required, Validators.minLength(2)]],
            email: [{ value: userEmail, disabled: true }, [Validators.required, Validators.email]],
            title: ['', [Validators.required, Validators.minLength(2)]],
            description: ['', [Validators.required, Validators.minLength(10)]],
        });

        this.formInitialized = true;
    }

    onSubmit() {
        if (this.supportTicketForm.valid) {
            this.isSubmitting = true;

            const raw = this.supportTicketForm.getRawValue();
            const preservedTitle = raw.title;
            const preservedDescription = raw.description;

            const formPayload: CreateTicketPayload = {
                name: raw.name,
                company: raw.company,
                email: raw.email,
                title: raw.title,
                description: raw.description,
                customerId: this.currentUser?._id || '',
                organizationId: this.organizationData?._id || ''
            };

            this.supportTicketService.createTicket(formPayload).subscribe(res => {
                if (res.success && res.data?.issueKey) {
                    const issueKey = res.data.issueKey;

                    if (this.attachmentFiles.length > 0) {
                        this.supportTicketService.uploadAttachments({ issueKey, files: this.attachmentFiles })
                            .subscribe(() => {
                                this.supportTicketService
                                    .sendSupportTicketEmailToCustomer(
                                        formPayload.email,
                                        issueKey,
                                        formPayload.name,
                                        preservedDescription,
                                        preservedTitle
                                    )
                                    .subscribe(emailRes => {
                                        if (emailRes.success) {
                                            this.toastService.success(emailRes.message || 'Email sent successfully');
                                        } else {
                                            this.toastService.error(emailRes.message || 'Failed to send email');
                                        }
                                    });

                                this.toastService.success(res.message || 'Support ticket created successfully');

                                this.supportTicketForm.patchValue({ title: '', description: '' });
                                this.supportTicketForm.get('title')?.markAsUntouched();
                                this.supportTicketForm.get('title')?.markAsPristine();
                                this.supportTicketForm.get('description')?.markAsUntouched();
                                this.supportTicketForm.get('description')?.markAsPristine();

                                this.clearAllAttachments();

                                if (this.isDialog) {
                                    this.ticketSubmitted.emit();
                                }

                                this.isSubmitting = false;
                            }, () => {
                                this.toastService.error('Failed to upload attachments');
                                this.isSubmitting = false;
                            });

                    } else {
                        this.supportTicketService
                            .sendSupportTicketEmailToCustomer(
                                formPayload.email,
                                issueKey,
                                formPayload.name,
                                preservedDescription,
                                preservedTitle
                            )
                            .subscribe(emailRes => {
                                if (emailRes.success) {
                                    this.toastService.success(emailRes.message || 'Email sent successfully');
                                } else {
                                    this.toastService.error(emailRes.message || 'Failed to send email');
                                }
                            });

                        this.toastService.success(res.message || 'Support ticket created successfully');

                        this.supportTicketForm.patchValue({ title: '', description: '' });
                        this.supportTicketForm.get('title')?.markAsUntouched();
                        this.supportTicketForm.get('title')?.markAsPristine();
                        this.supportTicketForm.get('description')?.markAsUntouched();
                        this.supportTicketForm.get('description')?.markAsPristine();

                        this.clearAllAttachments();

                        if (this.isDialog) {
                            this.ticketSubmitted.emit();
                        }

                        this.isSubmitting = false;
                    }

                } else {
                    this.toastService.error(res.message || 'Unable to create Jira ticket');
                    this.isSubmitting = false;
                }
            }, () => {
                this.toastService.error('Unable to create Jira ticket');
                this.isSubmitting = false;
            });
        }
    }


    isFieldInvalid(fieldName: string): boolean {
        const field = this.supportTicketForm.get(fieldName);
        return !!(field && field.invalid && (field.dirty || field.touched));
    }

    getFieldError(fieldName: string): string {
        const field = this.supportTicketForm.get(fieldName);
        if (field?.errors) {
            if (field.errors['required']) return `${fieldName} is required`;
            if (field.errors['email']) return 'Please enter a valid email address';
            if (field.errors['minlength']) return `${fieldName} must be at least ${field.errors['minlength'].requiredLength} characters`;
        }
        return '';
    }

    clearDescription() {
        if (this.supportTicketForm) {
            this.supportTicketForm.patchValue({ description: '' });
            const descriptionControl = this.supportTicketForm.get('description');
            descriptionControl?.markAsUntouched();
            descriptionControl?.markAsPristine();
        }
    }

    resetForm() {
        if (this.supportTicketForm) {
            this.supportTicketForm.patchValue({
                title: '',
                description: ''
            });

            const titleControl = this.supportTicketForm.get('title');
            const descriptionControl = this.supportTicketForm.get('description');
            titleControl?.markAsUntouched();
            titleControl?.markAsPristine();
            descriptionControl?.markAsUntouched();
            descriptionControl?.markAsPristine();
            this.clearAllAttachments();
        }
    }

    triggerFileInput() {
        this.fileInput.nativeElement.click();
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const newFiles = Array.from(input.files);
            this.attachmentFiles.push(...newFiles);
            newFiles.forEach(file => {
                const isImage = file.type.startsWith('image/');
                const isVideo = file.type.startsWith('video/');
                const preview = isImage ? URL.createObjectURL(file) : this.getFileIcon(file.type);
                this.attachmentPreviews.push({ file, preview, isImage, isVideo });
            });
            setTimeout(() => this.scrollToBottom(), 100);
        }
    }

    private getFileIcon(fileType: string): string {
        if (fileType.includes('pdf')) return '📄';
        if (fileType.includes('word') || fileType.includes('document')) return '📝';
        if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
        if (fileType.includes('video') || fileType.includes('webm') || fileType.includes('mp4') || fileType.includes('avi') || fileType.includes('mov')) return '🎥';
        return '📎';
    }

    removeAttachment(index: number) {
        if (this.attachmentPreviews[index] && this.attachmentPreviews[index].isImage) {
            URL.revokeObjectURL(this.attachmentPreviews[index].preview);
        }
        this.attachmentFiles.splice(index, 1);
        this.attachmentPreviews.splice(index, 1);
    }

    clearAllAttachments() {
        this.attachmentPreviews.forEach(item => {
            if (item.isImage) {
                URL.revokeObjectURL(item.preview);
            }
        });
        this.attachmentFiles = [];
        this.attachmentPreviews = [];
    }

    scrollToBottom() {
        if (this.scrollableContent && this.scrollableContent.nativeElement) {
            const element = this.scrollableContent.nativeElement;
            element.scrollTop = element.scrollHeight;
        }
    }
}
